import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import telefonlisteRaw from '../../data/telefonliste.json';
import { useAuth } from '../../context/AuthContext';
import telefonlisteService, { TelefonlisteEntry } from '../../services/telefonlisteService';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

/* ──────────────────────────────────────────────────────────────── */
/*  Types                                                          */
/* ──────────────────────────────────────────────────────────────── */

type SnackbarSeverity = 'success' | 'error' | 'info';

type SnackbarState = {
  message: string;
  severity: SnackbarSeverity;
} | null;

type ContactFormState = {
  name: string;
  phone: string;
  email: string;
  isHeader: boolean;
};

type ContactDraft = Partial<TelefonlisteEntry> & { name: string };

type EditingContact = {
  id: string;
  name: string;
  phone: string;
  email: string;
};

/* ──────────────────────────────────────────────────────────────── */
/*  Constants                                                       */
/* ──────────────────────────────────────────────────────────────── */

const DEFAULT_IMAGE_PATH = '/assets/drawable/ic_contact_placeholder.png';
const START_ROW_INDEX = 3;
const NAME_COLUMN_INDEX = 0;
const PHONE_COLUMN_INDEX = 3;
const EMAIL_COLUMN_INDEX = 5;

const cardSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  backgroundColor: 'rgba(255,255,255,0.92)',
  borderRadius: 3,
  padding: { xs: 1.5, md: 2.5 },
  boxShadow: '0 18px 40px rgba(13,53,89,0.16)',
  transition: 'box-shadow 0.2s',
  '&:hover': { boxShadow: '0 20px 50px rgba(13,53,89,0.22)' }
} as const;

const headerSx = {
  fontWeight: 700,
  color: '#F5F7FA',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.8
};

const paragraphSx = { color: '#233247', lineHeight: 1.6 } as const;

/* ──────────────────────────────────────────────────────────────── */
/*  Helpers                                                         */
/* ──────────────────────────────────────────────────────────────── */

const toDialLink = (value: string): string => {
  const digits = value.replace(/[^0-9+]/g, '');
  return `tel:${digits}`;
};

const toDownloadName = (value: string): string => {
  const sanitized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return sanitized || 'kontakt';
};

const toNormalizedString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const toOptionalString = (value: unknown): string | null => {
  const normalized = toNormalizedString(value);
  return normalized === '' ? null : normalized;
};

const createLocalId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizeContact = (draft: ContactDraft): TelefonlisteEntry => {
  const name = toNormalizedString(draft.name);
  let phone = toOptionalString(draft.phone ?? null);
  let email = toOptionalString(draft.email ?? null);
  let isHeader = Boolean(draft.isHeader);

  if (isHeader) {
    phone = null;
    email = null;
  } else if (!phone && !email) {
    isHeader = true;
  }

  return {
    id: toNormalizedString(draft.id ?? '') || createLocalId(),
    name,
    phone,
    email,
    isHeader,
    image: toOptionalString(draft.image ?? null) ?? DEFAULT_IMAGE_PATH
  };
};

const buildContactKey = (entry: TelefonlisteEntry): string => {
  const nameKey = entry.name.trim().toLowerCase();
  if (entry.isHeader) return `header::${nameKey}`;
  const phoneDigits = entry.phone ? entry.phone.replace(/[^0-9+]+/g, '') : '';
  const emailKey = entry.email ? entry.email.trim().toLowerCase() : '';
  return `${nameKey}|${phoneDigits}|${emailKey}`;
};

const dedupeContacts = (entries: TelefonlisteEntry[]): TelefonlisteEntry[] => {
  const result: TelefonlisteEntry[] = [];
  const seen = new Set<string>();
  entries.forEach(entry => {
    const normalized = normalizeContact(entry);
    const key = buildContactKey(normalized);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(normalized);
  });
  return result;
};

const parseExcelContacts = async (file: File): Promise<TelefonlisteEntry[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('Die Excel-Datei enthält kein Arbeitsblatt.');

  const sheet = workbook.Sheets[sheetName];
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, blankrows: false });
  const entries: TelefonlisteEntry[] = [];

  for (let rowIndex = START_ROW_INDEX; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row) continue;
    const name = toNormalizedString(row[NAME_COLUMN_INDEX]);
    if (!name) continue;
    const phone = toOptionalString(row[PHONE_COLUMN_INDEX]);
    const email = toOptionalString(row[EMAIL_COLUMN_INDEX]);
    entries.push(normalizeContact({ id: createLocalId(), name, phone, email, isHeader: !phone && !email, image: DEFAULT_IMAGE_PATH }));
  }

  return dedupeContacts(entries);
};

/**
 * Gruppiert die flache Kontaktliste in Sektionen.
 * Jede Sektion hat einen Header (oder "Ohne Zuordnung") und die dazugehoerigen Kontakte.
 */
type ContactSection = {
  header: TelefonlisteEntry | null;
  contacts: TelefonlisteEntry[];
};

const groupIntoSections = (entries: TelefonlisteEntry[]): ContactSection[] => {
  const sections: ContactSection[] = [];
  let current: ContactSection = { header: null, contacts: [] };

  entries.forEach(entry => {
    if (entry.isHeader) {
      // Vorherige Sektion abspeichern (wenn nicht leer oder erste)
      if (current.header || current.contacts.length > 0) {
        sections.push(current);
      }
      current = { header: entry, contacts: [] };
    } else {
      current.contacts.push(entry);
    }
  });

  // Letzte Sektion
  if (current.header || current.contacts.length > 0) {
    sections.push(current);
  }

  return sections;
};

/* ──────────────────────────────────────────────────────────────── */
/*  Component                                                       */
/* ──────────────────────────────────────────────────────────────── */

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    token,
    canManagePhonebook,
    canViewPhonebook,
    canEditPhonebook,
    canDeletePhonebook,
    canUploadPhonebookImages
  } = useAuth();

  const canEdit = canManagePhonebook || canEditPhonebook;
  const canDelete = canManagePhonebook || canDeletePhonebook;
  const canUploadImage = canManagePhonebook || canUploadPhonebookImages;

  const fallbackContacts = useMemo<TelefonlisteEntry[]>(() => {
    if (Array.isArray(telefonlisteRaw)) {
      return dedupeContacts((telefonlisteRaw as TelefonlisteEntry[]).map(entry => normalizeContact(entry)));
    }
    return [];
  }, []);

  const [contacts, setContacts] = useState<TelefonlisteEntry[]>(fallbackContacts);
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [form, setForm] = useState<ContactFormState>({ name: '', phone: '', email: '', isHeader: false });
  const [addToSection, setAddToSection] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingContact, setEditingContact] = useState<EditingContact | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<TelefonlisteEntry | null>(null);
  const [uploadingImageId, setUploadingImageId] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const imageTargetIdRef = useRef<string | null>(null);

  /* ── Daten laden ── */
  useEffect(() => {
    let active = true;
    const fetchContacts = async () => {
      setLoading(true);
      try {
        const response = await telefonlisteService.fetch(token);
        if (!active) return;
        const entries = Array.isArray(response.entries)
          ? dedupeContacts(response.entries.map(entry => normalizeContact(entry)))
          : fallbackContacts;
        setContacts(entries);
        setLastUpdated(response.updatedAt ?? null);
      } catch (error) {
        if (!active) return;
        setContacts(fallbackContacts);
        setSnackbar({ message: error instanceof Error ? error.message : 'Telefonliste konnte nicht geladen werden.', severity: 'error' });
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchContacts();
    return () => { active = false; };
  }, [token, fallbackContacts]);

  /* ── Persistenz ── */
  const persistChanges = useCallback(
    async (previous: TelefonlisteEntry[], next: TelefonlisteEntry[], message?: string) => {
      if (!canEdit) {
        setSnackbar({ message: 'Keine Berechtigung zum Bearbeiten der Telefonliste.', severity: 'error' });
        setContacts(previous);
        return;
      }
      if (!token) {
        setSnackbar({ message: 'Sitzung abgelaufen. Bitte erneut anmelden.', severity: 'error' });
        setContacts(previous);
        return;
      }
      setSaving(true);
      try {
        const response = await telefonlisteService.update(next, token);
        const sanitized = dedupeContacts(response.entries.map(entry => normalizeContact(entry)));
        setContacts(sanitized);
        setLastUpdated(response.updatedAt ?? null);
        if (message) setSnackbar({ message, severity: 'success' });
      } catch (error) {
        setContacts(previous);
        setSnackbar({ message: error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', severity: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [canEdit, token]
  );

  const applyChange = useCallback(
    (mutator: (current: TelefonlisteEntry[]) => { next: TelefonlisteEntry[]; message?: string }) => {
      if (!canEdit) {
        setSnackbar({ message: 'Keine Berechtigung zum Bearbeiten der Telefonliste.', severity: 'error' });
        return;
      }
      if (!token) {
        setSnackbar({ message: 'Bitte anmelden, um Änderungen zu speichern.', severity: 'error' });
        return;
      }
      setContacts(prev => {
        const snapshot = prev.map(entry => ({ ...entry }));
        const { next, message } = mutator(prev.map(entry => ({ ...entry })));
        const normalizedNext = dedupeContacts(next);
        const hasChanged =
          normalizedNext.length !== snapshot.length ||
          normalizedNext.some((entry, index) => {
            const previous = snapshot[index];
            if (!previous) return true;
            return entry.id !== previous.id || entry.name !== previous.name || entry.phone !== previous.phone || entry.email !== previous.email || entry.isHeader !== previous.isHeader || entry.image !== previous.image;
          });
        if (!hasChanged) {
          if (message) {
            const sev: SnackbarSeverity = message.toLowerCase().includes('keine') ? 'info' : 'success';
            setSnackbar({ message, severity: sev });
          }
          return prev;
        }
        void persistChanges(snapshot, normalizedNext, message);
        return normalizedNext;
      });
    },
    [canEdit, token, persistChanges]
  );

  /* ── Formular ── */
  const handleFormChange = (field: keyof ContactFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked, type } = event.target;
    setForm(prev => ({ ...prev, [field]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setForm({ name: '', phone: '', email: '', isHeader: false });
    setAddToSection(null);
  };

  const handleAddContact = () => {
    const name = form.name.trim();
    if (!name) {
      setSnackbar({ message: 'Bitte einen Namen angeben.', severity: 'error' });
      return;
    }
    if (!form.isHeader) {
      const hasDetails = form.phone.trim().length > 0 || form.email.trim().length > 0;
      if (!hasDetails) {
        setSnackbar({ message: 'Telefonnummer oder E-Mail sind erforderlich.', severity: 'error' });
        return;
      }
    }
    const newEntry = normalizeContact({
      id: createLocalId(),
      name: form.name,
      phone: form.phone,
      email: form.email,
      isHeader: form.isHeader,
      image: DEFAULT_IMAGE_PATH
    });

    applyChange(current => {
      const next = [...current];
      if (addToSection && !form.isHeader) {
        const headerIdx = next.findIndex(e => e.id === addToSection);
        if (headerIdx >= 0) {
          let insertIdx = headerIdx + 1;
          while (insertIdx < next.length && !next[insertIdx].isHeader) {
            insertIdx++;
          }
          next.splice(insertIdx, 0, newEntry);
          return { next, message: `${newEntry.name} wurde in Sektion eingefügt.` };
        }
      }
      next.push(newEntry);
      return { next, message: `${newEntry.name} wurde gespeichert.` };
    });
    resetForm();
  };

  /* ── Bearbeiten ── */
  const startEditing = (contact: TelefonlisteEntry) => {
    setEditingContact({
      id: contact.id,
      name: contact.name,
      phone: contact.phone || '',
      email: contact.email || ''
    });
  };

  const cancelEditing = () => setEditingContact(null);

  const saveEditing = () => {
    if (!editingContact) return;
    const { id, name, phone, email } = editingContact;
    if (!name.trim()) {
      setSnackbar({ message: 'Name darf nicht leer sein.', severity: 'error' });
      return;
    }
    applyChange(current => ({
      next: current.map(c => c.id === id ? { ...c, name: name.trim(), phone: phone.trim() || null, email: email.trim() || null } : c),
      message: `${name.trim()} wurde aktualisiert.`
    }));
    setEditingContact(null);
  };

  /* ── Löschen ── */
  const handleDeleteContact = (entry: TelefonlisteEntry) => {
    setDeleteConfirm(entry);
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    const entry = deleteConfirm;
    applyChange(current => ({
      next: current.filter(item => item.id !== entry.id),
      message: `${entry.name} wurde entfernt.`
    }));
    setDeleteConfirm(null);
  };

  /* ── Bild-Upload ── */
  const handleImageUploadClick = (contactId: string) => {
    imageTargetIdRef.current = contactId;
    imageInputRef.current?.click();
  };

  const handleImageFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const contactId = imageTargetIdRef.current;
    if (!file || !contactId || !token) return;

    setUploadingImageId(contactId);
    try {
      const result = await telefonlisteService.uploadContactImage(contactId, file, token);
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, image: result.imageUrl } : c));
      setSnackbar({ message: 'Kontaktbild wurde aktualisiert.', severity: 'success' });
    } catch (error) {
      setSnackbar({ message: error instanceof Error ? error.message : 'Bild-Upload fehlgeschlagen.', severity: 'error' });
    } finally {
      setUploadingImageId(null);
      imageTargetIdRef.current = null;
    }
  };

  /* ── Excel-Import ── */
  const handleUploadExcel = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const parsed = await parseExcelContacts(file);
      if (!parsed.length) {
        setSnackbar({ message: 'Die Excel-Datei enthält keine neuen Einträge.', severity: 'info' });
        return;
      }
      applyChange(current => {
        const map = new Map<string, TelefonlisteEntry>();
        current.forEach(entry => map.set(buildContactKey(entry), entry));
        let added = 0;
        let updated = 0;
        const next = current.map(entry => ({ ...entry }));
        parsed.forEach(entry => {
          const key = buildContactKey(entry);
          const existing = map.get(key);
          if (existing) {
            const needsUpdate = existing.name !== entry.name || existing.phone !== entry.phone || existing.email !== entry.email || existing.isHeader !== entry.isHeader;
            if (needsUpdate) {
              const merged: TelefonlisteEntry = { ...existing, ...entry, id: existing.id, image: existing.image || entry.image };
              const index = next.findIndex(item => item.id === existing.id);
              if (index !== -1) next[index] = merged;
              map.set(key, merged);
              updated += 1;
            }
          } else {
            next.push(entry);
            map.set(key, entry);
            added += 1;
          }
        });
        const parts: string[] = [];
        if (added > 0) parts.push(`${added} neue Kontakte hinzugefügt`);
        if (updated > 0) parts.push(`${updated} Kontakte aktualisiert`);
        const message = parts.length ? `${parts.join(' und ')}.` : 'Keine Änderungen übernommen.';
        return { next, message };
      });
    } catch (error) {
      setSnackbar({ message: error instanceof Error ? error.message : 'Import der Excel-Datei fehlgeschlagen.', severity: 'error' });
    }
  };

  /* ── vCard-Export ── */
  const handleSaveContact = (contact: TelefonlisteEntry) => {
    if (contact.isHeader) {
      setSnackbar({ message: 'Überschriften können nicht gespeichert werden.', severity: 'info' });
      return;
    }
    const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${contact.name}`];
    if (contact.phone) {
      const digits = contact.phone.replace(/[^0-9+]/g, '');
      if (digits) lines.push(`TEL;TYPE=WORK,VOICE:${digits}`);
    }
    if (contact.email) lines.push(`EMAIL;TYPE=INTERNET,WORK:${contact.email}`);
    lines.push('END:VCARD');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${toDownloadName(contact.name)}.vcf`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    setSnackbar({ message: `vCard für ${contact.name} heruntergeladen.`, severity: 'success' });
  };

  /* ── Sektionen aufbauen ── */
  const sections = useMemo(() => groupIntoSections(contacts), [contacts]);

  /* ── Suchfilter ── */
  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map(section => ({
        ...section,
        contacts: section.contacts.filter(c =>
          c.name.toLowerCase().includes(q) ||
          (c.phone && c.phone.toLowerCase().includes(q)) ||
          (c.email && c.email.toLowerCase().includes(q))
        )
      }))
      .filter(section => section.contacts.length > 0 || (section.header && section.header.name.toLowerCase().includes(q)));
  }, [sections, searchQuery]);

  /* Header-Optionen fürs Dropdown "In Sektion einfügen" */
  const headerOptions = useMemo(() => sections
    .filter(s => s.header)
    .map(s => ({ id: s.header!.id, name: s.header!.name })),
    [sections]
  );

  /* ── Versteckte Dateiinputs ── */
  const hiddenImageInput = (
    <input
      ref={imageInputRef}
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      hidden
      onChange={handleImageFileChange}
    />
  );

  /* ── Keine Berechtigung ── */
  if (!canViewPhonebook && !canManagePhonebook) {
    return (
      <UnitPageShell title="Telefonliste" showTTS={false}>
        <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%', py: 4 }}>
          <Alert severity="error">Keine Berechtigung, die Telefonliste anzuzeigen.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  /* ──────────────────────────────────────────────────────────────── */
  /*  Render                                                         */
  /* ──────────────────────────────────────────────────────────────── */

  return (
    <UnitPageShell title="Telefonliste" showTTS={false}>
      {hiddenImageInput}
      <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>

        {/* ── Admin-Bereich ── */}
        {canEdit && (
          <Accordion
            defaultExpanded={false}
            sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', '&:before': { display: 'none' } }}
            elevation={4}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                Verwaltung
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2.5}>
                {/* Neuen Kontakt hinzufügen */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Neuen Eintrag hinzufügen
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Name" value={form.name} onChange={handleFormChange('name')} disabled={saving} size="small" />
                  <TextField fullWidth label="Telefon" value={form.phone} onChange={handleFormChange('phone')} disabled={saving || form.isHeader} size="small" />
                  <TextField fullWidth label="E-Mail" value={form.email} onChange={handleFormChange('email')} disabled={saving || form.isHeader} size="small" />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <FormControlLabel
                    control={<Switch checked={form.isHeader} onChange={handleFormChange('isHeader')} disabled={saving} size="small" />}
                    label="Als Überschrift/Abteilung"
                  />
                  {!form.isHeader && headerOptions.length > 0 && (
                    <TextField
                      select
                      label="In Sektion einfügen"
                      value={addToSection || ''}
                      onChange={(e) => setAddToSection(e.target.value || null)}
                      size="small"
                      sx={{ minWidth: 200 }}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Am Ende anfügen</option>
                      {headerOptions.map(h => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                    </TextField>
                  )}
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} onClick={handleAddContact} disabled={saving} size="small">
                    Hinzufügen
                  </Button>
                  <Button variant="text" onClick={resetForm} disabled={saving} size="small">
                    Zurücksetzen
                  </Button>
                </Stack>

                <Divider />

                {/* Excel-Import */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={saving} size="small">
                    Excel importieren
                    <input type="file" accept=".xlsx,.xls" hidden onChange={handleUploadExcel} />
                  </Button>
                  {saving && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" sx={{ color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                        Änderungen werden gespeichert …
                      </Typography>
                    </Stack>
                  )}
                  {!saving && lastUpdated && (
                    <Typography variant="body2" sx={{ color: '#233247' }}>
                      Letzte Aktualisierung: {new Date(lastUpdated * 1000).toLocaleString('de-DE')}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* ── Suchleiste ── */}
        <TextField
          fullWidth
          placeholder="Kontakt suchen …"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
        />

        {/* ── Kontaktliste ── */}
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2, color: '#FFFFFF' }}>
              Telefonliste wird geladen …
            </Typography>
          </Stack>
        ) : filteredSections.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? 'Keine Kontakte gefunden.' : 'Die Telefonliste ist leer.'}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={3}>
            {filteredSections.map((section, sIdx) => (
              <Box key={section.header?.id || `section-${sIdx}`}>
                {/* Sektions-Header */}
                {section.header && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: sIdx === 0 ? 0 : 1, mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={headerSx}>
                      {section.header.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      ({section.contacts.length})
                    </Typography>
                    {canDelete && (
                      <Tooltip title="Überschrift löschen">
                        <IconButton size="small" onClick={() => handleDeleteContact(section.header!)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}

                {/* Kontakte in der Sektion */}
                <Stack spacing={1.5}>
                  {section.contacts.map(contact => {
                    const isEditing = editingContact?.id === contact.id;
                    const isUploadingImage = uploadingImageId === contact.id;

                    return (
                      <Box key={contact.id} sx={cardSx}>
                        {/* Avatar mit Upload-Overlay */}
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            canUploadImage && !isEditing ? (
                              <Tooltip title="Bild ändern">
                                <IconButton
                                  size="small"
                                  onClick={() => handleImageUploadClick(contact.id)}
                                  disabled={isUploadingImage}
                                  sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    width: 24,
                                    height: 24,
                                    '&:hover': { bgcolor: 'primary.dark' }
                                  }}
                                >
                                  {isUploadingImage ? <CircularProgress size={14} color="inherit" /> : <CameraAltIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </Tooltip>
                            ) : null
                          }
                        >
                          <Avatar
                            src={contact.image}
                            alt={contact.name}
                            sx={{ width: { xs: 56, md: 64 }, height: { xs: 56, md: 64 } }}
                          >
                            {contact.name.charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>

                        {/* Kontaktdaten oder Bearbeitungsmodus */}
                        {isEditing && editingContact ? (
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                              size="small"
                              label="Name"
                              value={editingContact.name}
                              onChange={(e) => setEditingContact(prev => prev ? { ...prev, name: e.target.value } : prev)}
                              fullWidth
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <TextField
                                size="small"
                                label="Telefon"
                                value={editingContact.phone}
                                onChange={(e) => setEditingContact(prev => prev ? { ...prev, phone: e.target.value } : prev)}
                                fullWidth
                              />
                              <TextField
                                size="small"
                                label="E-Mail"
                                value={editingContact.email}
                                onChange={(e) => setEditingContact(prev => prev ? { ...prev, email: e.target.value } : prev)}
                                fullWidth
                              />
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={saveEditing} disabled={saving}>
                                Speichern
                              </Button>
                              <Button size="small" variant="text" startIcon={<CloseIcon />} onClick={cancelEditing}>
                                Abbrechen
                              </Button>
                            </Stack>
                          </Box>
                        ) : (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.15rem' }, color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                              {contact.name}
                            </Typography>
                            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                              {contact.phone && (
                                <Typography component="a" href={toDialLink(contact.phone)} variant="body2" sx={{ ...paragraphSx, textDecoration: 'none' }}>
                                  📞 {contact.phone}
                                </Typography>
                              )}
                              {contact.email && (
                                <Typography component="a" href={`mailto:${contact.email}`} variant="body2" sx={{ ...paragraphSx, textDecoration: 'none' }}>
                                  ✉️ {contact.email}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}

                        {/* Aktions-Buttons */}
                        {!isEditing && (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                            <Tooltip title="Als vCard speichern">
                              <span>
                                <IconButton onClick={() => handleSaveContact(contact)} color="primary" size="small">
                                  <SaveAltIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Bearbeiten">
                                <span>
                                  <IconButton onClick={() => startEditing(contact)} size="small" disabled={saving}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Löschen">
                                <span>
                                  <IconButton onClick={() => handleDeleteContact(contact)} size="small" disabled={saving} color="error">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>

                {/* In diese Sektion einfügen Button */}
                {canEdit && section.header && (
                  <Collapse in={addToSection === section.header.id}>
                    <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                      <Typography variant="caption" color="primary">
                        Neuer Kontakt wird in «{section.header.name}» eingefügt
                      </Typography>
                    </Box>
                  </Collapse>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* ── Lösch-Dialog ── */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Eintrag löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Soll <strong>{deleteConfirm?.name}</strong> wirklich unwiderruflich gelöscht werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </UnitPageShell>
  );
};

export default Page;
